import { useSubscription } from "@apollo/client/react";
import type { DAOCoinLimitOrderEntryResponse } from "deso-protocol";
import { DAOCoinLimitOrderOperationTypeString } from "deso-protocol";
import { uniqBy } from "es-toolkit";
import { useEffect, useMemo, useState } from "react";

import { getDaoCoinLimitOrders } from "@/backend/deso";
import { createToastErrorDetails } from "@/components/ui/toast-helpers";
import { toast } from "@/components/ui/use-toast";
import type { DesoTokenLimitOrder } from "@/graphql/codegen/graphql";
import { DeSoTokenLimitOrderSubscriptionDocument } from "@/graphql/codegen/graphql";
import { DESO_TOKEN_DISPLAY_NAME } from "@/utils/constants";
import { nanosToInteger, tokenNanosToInteger } from "@/utils/currency";
import { gqlWsOrderToTokenLimitEntry } from "@/utils/orderbook";

import { getTopicForTwoPublicKeys, TopicPrefix } from "./use-subscriptions";

interface TokenOrderbookProps {
  baseCurrencyPublicKeyBase58Check?: string;
  quoteCurrencyPublicKeyBase58Check?: string;
  transactorPublicKeyBase58Check?: string;
}

export interface TokenOrderbook {
  bids: Array<TokenOrder>;
  asks: Array<TokenOrder>;
}

export interface TokenOrder {
  AmountInQuoteCurrency: number;
  PriceInQuoteCurrency: number;
  OperationType: BID_OR_ASK_OP;
  TransactorPublicKeyBase58Check?: string;
  OrderId?: string;
}

export enum BID_OR_ASK_OP {
  ASK_OP = 1,
  BID_OP = 2,
}

export const ZERO = BigInt("0");
const TEN = BigInt("10");
export const oneE9 = BigInt("1000000000");
export const oneE18 = BigInt("1000000000000000000");
export const oneE38 = BigInt("100000000000000000000000000000000000000");
export const oneE76 = oneE38 * oneE38;

const isOrderSellingDESO = (order: DesoTokenLimitOrder) => {
  return !order.creatorSoldAccount?.publicKey;
};

const isOrderBuyingDESO = (order: DesoTokenLimitOrder) => {
  return !order.creatorBoughtAccount?.publicKey;
};

const getOrderBuyingPublicKey = (order: DesoTokenLimitOrder) => {
  return order.creatorBoughtAccount?.publicKey ?? DESO_TOKEN_DISPLAY_NAME;
};

const getOrderSellingPublicKey = (order: DesoTokenLimitOrder) => {
  return order.creatorSoldAccount?.publicKey ?? DESO_TOKEN_DISPLAY_NAME;
};

const getBaseUnitsToSell = (order: DesoTokenLimitOrder) => {
  // For ask orders, quantity to sell is just quantity to fill in base units
  if (order.operationType === BID_OR_ASK_OP.ASK_OP) {
    return BigInt(order.quantityToFillInBaseUnitsNumeric ?? "0");
  }
  return (
    (BigInt(order.quantityToFillInBaseUnitsNumeric ?? "0") *
      BigInt(order.scaledExchangeRateCoinsToSellPerCoinToBuyNumeric ?? "0")) /
    oneE38
  );
};

function getNumDigits(val: bigint): number {
  let numDigits = 0;
  while (val !== ZERO) {
    ++numDigits;
    val = val / TEN;
  }
  return numDigits;
}

export function convertBigIntToDecimalString(
  val: bigint,
  scalingFactor: bigint,
  toFixedDecimalPlaces = -1,
): string {
  const wholeNumber = val / scalingFactor;
  const decimalPart = val % scalingFactor;

  if (decimalPart === ZERO || toFixedDecimalPlaces === 0) {
    return wholeNumber.toString();
  }

  const scalingFactorDigits = getNumDigits(scalingFactor);
  let decimalPartAsString = decimalPart.toString();

  // Left pad the decimal part with zeros
  if (decimalPartAsString.length !== scalingFactorDigits) {
    const decimalLeadingZeros = "0".repeat(
      scalingFactorDigits - decimalPartAsString.length - 1,
    );
    decimalPartAsString = `${decimalLeadingZeros}${decimalPartAsString}`;
  }

  // Trim trailing zeros
  decimalPartAsString = decimalPartAsString.replace(/\.0+$/, "");

  // Pad with additional trailing zeros if needed
  if (toFixedDecimalPlaces > 0) {
    decimalPartAsString =
      decimalPartAsString + "0".repeat(toFixedDecimalPlaces);
    decimalPartAsString = decimalPartAsString.substring(
      0,
      toFixedDecimalPlaces,
    );
  }

  return `${wholeNumber}.${decimalPartAsString}`;
}

export function quantityBigIntToDecmialString(
  quantity: bigint,
  toFixedDecimalPlaces = -1,
  scalingFactor: bigint = oneE18,
): string {
  return convertBigIntToDecimalString(
    quantity,
    scalingFactor,
    toFixedDecimalPlaces,
  );
}

export function parseFloatWithCommas(f: string): number {
  return parseFloat(f.replace(/,/g, ""));
}

export function stripCommas(f: string): string {
  if (f.indexOf("e") > -1) {
    f = parseFloatWithCommas(f).toFixed(38);
  }
  return f.replace(/,/g, "");
}

export function convertDecimalStringToBigInt(
  inputDecimalString: string,
  scalingFactor: bigint,
): bigint {
  let decimalString = stripCommas(inputDecimalString);
  if (inputDecimalString.indexOf("e") > -1) {
    decimalString = parseFloatWithCommas(inputDecimalString).toLocaleString(
      "en-US",
      {
        maximumFractionDigits: 38,
        useGrouping: false,
      },
    );
  }
  let vals = decimalString.split(".");
  if (vals.length === 0) {
    vals = ["0", "0"];
  }
  // In this case, we had a whole number like 123, with no decimal
  // so we add a "0" as the decimal.
  if (vals.length !== 2) {
    vals.push("0");
  }
  // This can happen if we have something like ".123"
  if (vals[0] === "") {
    vals[0] = "0";
  }
  // This can happen if we have something like "123."
  if (vals[1] === "") {
    vals[1] = "0";
  }

  const numDecimalPlacesInString = vals[1].length;

  const wholePart = BigInt(vals[0]) * scalingFactor;
  let decimalPart = BigInt(vals[1]) * scalingFactor;

  // Given an input string 1.123, this produces
  // decimalPart = 123 * scalingFactor * (10 ^ -numDecimalPlacesInString)
  for (let i = 0; i < numDecimalPlacesInString; ++i) {
    decimalPart = decimalPart / TEN;
  }

  return wholePart + decimalPart;
}

export function priceDecimalStringToBigInt(price: string): bigint {
  return convertDecimalStringToBigInt(price, oneE38);
}

export function invertPrice(val: bigint): bigint {
  // Ex: say val = 3 but is multiplied by a scaling factor of 100, so val = 300 as a bigint
  //
  // If we want to invert 3 to compute 1/3 but want to do it using the scaled bigints,
  // we can use the math (100 * 100 / 300) = 33, which scales back down to 0.33
  //
  // This function achieves the same thing but for numbers with a scaling factor of 1e38
  return (oneE38 * oneE38) / val;
}

export function quantityDecimalStringToBigInt(
  quantity: string,
  scalingFactor: bigint = oneE18,
): bigint {
  return convertDecimalStringToBigInt(quantity, scalingFactor);
}

export function mulPriceAndQuantity(price: bigint, quantity: bigint): bigint {
  return (price * quantity) / oneE38;
}

export function quantityBigIntToFloat(quantity: bigint): number {
  return parseFloatWithCommas(quantityBigIntToDecmialString(quantity));
}

export function priceBigIntToDecmialString(
  price: bigint,
  toFixedDecimalPlaces = -1,
): string {
  return convertBigIntToDecimalString(price, oneE38, toFixedDecimalPlaces);
}

export function priceBigIntToFloat(price: bigint): number {
  return parseFloatWithCommas(priceBigIntToDecmialString(price));
}

// This calculates price as a bigint that's scaled up by 1e38 and then converts it back to a human-readable decimal string.
//  The denominator for the output price is determined by the operation type:
// - If operation type = BID, then price is the number of selling coins per buying coin
// - If operation type = ASK, then price is the number of buying coins per selling coin
function scaledExchangeRateToPriceString(
  scaledExchangeRate: string,
  buyingPublicKey: string,
  sellingPublicKey: string,
  operationType: "BID" | "ASK",
): string {
  let price = BigInt(scaledExchangeRate);
  if (buyingPublicKey === "DESO") {
    price = price / oneE9;
  } else if (sellingPublicKey === "DESO") {
    price = price * oneE9;
  }

  if (operationType === "ASK") {
    price = oneE76 / price;
  }

  return convertBigIntToDecimalString(price, oneE38);
}

export type BID_OR_ASK = typeof BID | typeof ASK;

export function operationTypeToBidOrAskString(
  operationType: number | null | undefined,
): BID_OR_ASK {
  return bidOrAskToString(bidOrAsk(operationType));
}

// Side
export const BID =
  DAOCoinLimitOrderOperationTypeString.DAOCoinLimitOrderOperationTypeStringBID;
export const ASK =
  DAOCoinLimitOrderOperationTypeString.DAOCoinLimitOrderOperationTypeStringASK;

export function bidOrAskToString(bidOrAskVal: BID_OR_ASK_OP): BID_OR_ASK {
  switch (bidOrAskVal) {
    case BID_OR_ASK_OP.ASK_OP:
      return ASK;
    case BID_OR_ASK_OP.BID_OP:
      return BID;
    default:
      throw new Error(`Unknown bidOrAsk: ${bidOrAskVal}`);
  }
}

export function bidOrAsk(
  operationType: number | null | undefined,
): BID_OR_ASK_OP {
  switch (operationType) {
    case 1:
      return BID_OR_ASK_OP.ASK_OP;
    case 2:
      return BID_OR_ASK_OP.BID_OP;
    default:
      throw new Error(`Unknown operationType: ${operationType}`);
  }
}

// This calculates ExchangeRateCoinsToSellPerCoinsToBuy as an unscaled coin-level exchange rate for the coin pair.
// The value is always denominated as the number of selling coins per buying coin.
function scaledExchangeRateToUnscaledFloat(
  scaledExchangeRateStr: string,
  buyingPublicKey: string,
  sellingPublicKey: string,
) {
  let scaledExchangeRate = BigInt(scaledExchangeRateStr);
  if (buyingPublicKey === "DESO") {
    scaledExchangeRate = scaledExchangeRate / oneE9;
  } else if (sellingPublicKey === "DESO") {
    scaledExchangeRate = scaledExchangeRate * oneE9;
  }

  return Number(convertBigIntToDecimalString(scaledExchangeRate, oneE38));
}

export const parseDesoTokenLimitOrder = (
  orders: Array<DesoTokenLimitOrder>,
) => {
  // Aggregate orders such that we have a map of keys representing transactor + selling coin
  // mapped to an array of orders.
  const aggregatedOrders = orders.reduce(
    (acc: { [k: string]: DesoTokenLimitOrder[] }, cur) => {
      if (
        !(cur?.transactorAccount?.publicKey && cur?.sellingDaoCoinCreatorPkid)
      ) {
        // this will never happen, but it makes typescript happy.
        return acc;
      }
      const key =
        cur.transactorAccount.publicKey + cur.sellingDaoCoinCreatorPkid;
      if (!(key in acc)) {
        acc[key] = [];
      }
      acc[key].push(cur);
      return acc;
    },
    {},
  );

  // We'll keep track of all the valid orders in one array.
  const validOrders = [] as DesoTokenLimitOrder[];
  Object.entries(aggregatedOrders).forEach(([, transactorOrders]) => {
    if (!transactorOrders.length) {
      return;
    }

    // Sort the orders by priority (lowest to highest price, breaking ties w/ block height).
    transactorOrders.sort((a, b) => {
      // We use the scaled exchange rate to sort limit orders in the sequence they would be matched
      // against orders on the opposite side of the book. The coin to sell is on the numerator side of
      // the exchange rate, meaning higher exchange rates have a higher precedence in the the order
      // book and will be matched first.
      const diff =
        BigInt(a.scaledExchangeRateCoinsToSellPerCoinToBuyNumeric ?? "0") -
        BigInt(b.scaledExchangeRateCoinsToSellPerCoinToBuyNumeric ?? "0");
      if (diff > 0) {
        return -1;
      }
      if (diff < 0) {
        return 1;
      }
      return Number(a.blockHeight) - Number(b.blockHeight);
    });

    // Keep track of the running balance of the transactor's account.
    let runningBalance = isOrderSellingDESO(transactorOrders[0])
      ? BigInt(
          transactorOrders[0].transactorAccount?.desoBalance?.balanceNanos ??
            "0",
        )
      : BigInt(
          transactorOrders[0].transactorSellingTokenBalance?.balanceNanos ??
            "0",
        );

    // For each order, get the units to sell and check if we have enough balance to fill it.
    // If we don't have enough balance, we can't fill any more orders and will break.
    // If we do have enough balance, append it to the list of valid orders and subtract the
    // units to sell from the running balance.
    for (const order of transactorOrders) {
      if (runningBalance === ZERO) {
        return;
      }
      const unitsToSell = getBaseUnitsToSell(order);
      if (unitsToSell > runningBalance) {
        const quantityToFillInBaseUnitsNumeric =
          order.operationType === BID_OR_ASK_OP.ASK_OP
            ? runningBalance
            : (runningBalance * oneE38) /
              BigInt(
                order.scaledExchangeRateCoinsToSellPerCoinToBuyNumeric ?? "0",
              );
        validOrders.push({
          ...order,
          quantityToFillInBaseUnitsNumeric:
            quantityToFillInBaseUnitsNumeric.toString(),
        });
        return;
      }
      runningBalance -= unitsToSell;
      validOrders.push(order);
    }
  });

  return validOrders.map((order: DesoTokenLimitOrder) => {
    const isBid = order.operationType === BID_OR_ASK_OP.BID_OP;
    const isBuyingDESO = isOrderBuyingDESO(order);
    const isSellingDESO = isOrderSellingDESO(order);

    const isDesoDenominated =
      (isBuyingDESO && isBid) || (isSellingDESO && !isBid);
    const scaledExchangeRate =
      order.scaledExchangeRateCoinsToSellPerCoinToBuyNumeric ?? "";
    const buyingPublicKey = getOrderBuyingPublicKey(order);
    const sellingPublicKey = getOrderSellingPublicKey(order);
    const quantityToFillInBaseUnitsNumeric =
      order.quantityToFillInBaseUnitsNumeric ?? "0";
    return {
      TransactorPublicKeyBase58Check: order.transactorAccount?.publicKey,
      BuyingDAOCoinCreatorPublicKeyBase58Check: buyingPublicKey,
      SellingDAOCoinCreatorPublicKeyBase58Check: sellingPublicKey,
      Quantity: isDesoDenominated
        ? quantityBigIntToDecmialString(
            BigInt(quantityToFillInBaseUnitsNumeric),
            9,
            oneE9,
          )
        : quantityBigIntToDecmialString(
            BigInt(quantityToFillInBaseUnitsNumeric),
            18,
            oneE18,
          ),
      QuantityToFill: isDesoDenominated
        ? nanosToInteger(quantityToFillInBaseUnitsNumeric)
        : tokenNanosToInteger(quantityToFillInBaseUnitsNumeric),
      Price: scaledExchangeRateToPriceString(
        scaledExchangeRate,
        buyingPublicKey,
        sellingPublicKey,
        operationTypeToBidOrAskString(order.operationType),
      ),
      ExchangeRateCoinsToSellPerCoinToBuy: scaledExchangeRateToUnscaledFloat(
        scaledExchangeRate,
        buyingPublicKey,
        sellingPublicKey,
      ),
      OperationType: operationTypeToBidOrAskString(order.operationType),
      OrderID: order.orderId,
    };
  });
};

export function useTokenOrderbook({
  baseCurrencyPublicKeyBase58Check,
  quoteCurrencyPublicKeyBase58Check,
}: TokenOrderbookProps) {
  const [tokenOrderbook, setTokenOrderbook] = useState<
    Array<DAOCoinLimitOrderEntryResponse>
  >([]);
  const [loading, setLoading] = useState(false);

  const topic = useMemo(() => {
    return getTopicForTwoPublicKeys(
      TopicPrefix.DAOCoinLimitOrderEntries,
      (baseCurrencyPublicKeyBase58Check === DESO_TOKEN_DISPLAY_NAME
        ? process.env.NEXT_PUBLIC_DESO_ZERO_PUBLIC_KEY
        : baseCurrencyPublicKeyBase58Check) || "",
      quoteCurrencyPublicKeyBase58Check ?? "",
    );
  }, [baseCurrencyPublicKeyBase58Check, quoteCurrencyPublicKeyBase58Check]);

  useSubscription(DeSoTokenLimitOrderSubscriptionDocument, {
    variables: {
      topic,
    },
    onData: ({ data }) => {
      if (!data) return;
      const operation = data.data?.listen?.operation;

      const orderId = JSON.parse(
        atob(data.data?.listen.relatedNodeId ?? ""),
      )[1].slice(-64);

      // For DELETE operations, we can exit early if the order doesn't exist in the selectedTokenOrderBook.
      // For INSERT and UPDATE operations, we can exit early if the order already exists in the selectedTokenOrderBook
      // and is effectively the same as the order currently in the selectedTokenOrderBook.
      const existingOrder = tokenOrderbook.find(
        (order) => order.OrderID === orderId,
      );

      if (operation === "DELETE" && !existingOrder) {
        return;
      }

      const newOrder = gqlWsOrderToTokenLimitEntry(data.data);

      if ((operation === "INSERT" || operation === "UPDATE") && existingOrder) {
        if (
          existingOrder.Price === newOrder?.Price &&
          existingOrder.Quantity === newOrder?.Quantity
        ) {
          return;
        }
      }

      setTokenOrderbook((prev) => {
        if (!orderId || !newOrder) {
          return prev;
        }

        if (operation === "DELETE") {
          return prev.filter((order) => order.OrderID !== orderId);
        }
        if (operation === "UPDATE") {
          // if the order is already in the order book, we update it. Otherwise,
          // we treat it as an insert.
          if (prev.find((order) => order.OrderID === orderId)) {
            return prev.map((order) =>
              order.OrderID === orderId ? newOrder : order,
            );
          }
        }

        const uniqOrders = uniqBy(
          [...prev, newOrder],
          (e) => e?.OrderID as string,
        );
        return uniqOrders;
      });
    },
  });

  useEffect(() => {
    const run = async () => {
      if (
        !baseCurrencyPublicKeyBase58Check ||
        !quoteCurrencyPublicKeyBase58Check
      )
        return;

      setLoading(true);

      try {
        const data = await getDaoCoinLimitOrders({
          DAOCoin1CreatorPublicKeyBase58Check:
            quoteCurrencyPublicKeyBase58Check,
          DAOCoin2CreatorPublicKeyBase58Check: baseCurrencyPublicKeyBase58Check,
        });

        const allOrders = data.Orders || [];

        setTokenOrderbook(allOrders);
      } catch (error) {
        toast({
          variant: "destructive",
          title: "Error",
          description: `An error happened when getting token orderbook`,
          details: createToastErrorDetails(error),
        });
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [baseCurrencyPublicKeyBase58Check, quoteCurrencyPublicKeyBase58Check]);

  const askOrders: Array<TokenOrder> = useMemo(
    () =>
      tokenOrderbook
        .filter(
          (e) =>
            e.SellingDAOCoinCreatorPublicKeyBase58Check ===
            baseCurrencyPublicKeyBase58Check,
        )
        .map((e) => {
          const rawOrderPrice = priceDecimalStringToBigInt(e.Price);
          const rawOrderQuantity = quantityDecimalStringToBigInt(e.Quantity);

          const isAsk = e.OperationType === "ASK";
          const price = isAsk ? rawOrderPrice : invertPrice(rawOrderPrice);
          const quantity = isAsk
            ? mulPriceAndQuantity(rawOrderPrice, rawOrderQuantity)
            : rawOrderQuantity;
          return {
            AmountInQuoteCurrency: quantityBigIntToFloat(quantity),
            PriceInQuoteCurrency: priceBigIntToFloat(price),
            OperationType: BID_OR_ASK_OP.ASK_OP,
            TransactorPublicKeyBase58Check: e.TransactorPublicKeyBase58Check,
            OrderId: e.OrderID,
          };
        })
        .sort((a, b) => b.PriceInQuoteCurrency - a.PriceInQuoteCurrency),
    [tokenOrderbook, baseCurrencyPublicKeyBase58Check],
  );

  const bidOrders: Array<TokenOrder> = useMemo(
    () =>
      tokenOrderbook
        .filter(
          (e) =>
            e.BuyingDAOCoinCreatorPublicKeyBase58Check ===
            baseCurrencyPublicKeyBase58Check,
        )
        .map((e) => {
          const rawOrderPrice = priceDecimalStringToBigInt(e.Price);
          const rawOrderQuantity = quantityDecimalStringToBigInt(e.Quantity);

          const isBid = e.OperationType === "BID";
          const price = isBid ? rawOrderPrice : invertPrice(rawOrderPrice);
          const quantity = isBid
            ? mulPriceAndQuantity(rawOrderPrice, rawOrderQuantity)
            : rawOrderQuantity;

          return {
            AmountInQuoteCurrency: quantityBigIntToFloat(quantity),
            PriceInQuoteCurrency: priceBigIntToFloat(price),
            OperationType: BID_OR_ASK_OP.BID_OP,
            TransactorPublicKeyBase58Check: e.TransactorPublicKeyBase58Check,
            OrderId: e.OrderID,
          };
        })
        .sort((a, b) => b.PriceInQuoteCurrency - a.PriceInQuoteCurrency),
    [tokenOrderbook, baseCurrencyPublicKeyBase58Check],
  );

  return {
    tokenOrderbook: {
      asks: askOrders.sort(
        (a, b) => b.PriceInQuoteCurrency - a.PriceInQuoteCurrency,
      ),
      bids: bidOrders.sort(
        (a, b) => b.PriceInQuoteCurrency - a.PriceInQuoteCurrency,
      ),
    },
    loading,
  };
}
